import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalTime } from '@/components/generals/local-time'
import { SectionLink } from '@/components/generals/section-link'
import { NAV_ITEMS, QUOTE_HASH, siteConfig } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onNavigate: () => void
    onClose: () => void
}

export const MobileMenu: FC<MobileMenuProps> = ({ open, onNavigate, onClose }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        const first = ref.current?.querySelector<HTMLElement>('a')

        first?.focus({ preventScroll: true })
        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    return (
        <div
            ref={ref}
            id='site-menu'
            role='dialog'
            aria-modal='true'
            aria-label="Site menu"
            className={cn(
                'fixed inset-0 z-40 flex flex-col justify-between bg-ink px-(--gutter) pb-8 pt-[calc(var(--header-h)+2rem)] text-paper lg:hidden',
                'transition-[clip-path,visibility] duration-700 ease-in-out-quart',
                open ? 'visible [clip-path:inset(0_0_0_0)]' : 'invisible [clip-path:inset(0_0_100%_0)]'
            )}
        >
            <nav aria-label="Mobile">
                <ul className='flex flex-col'>
                    {NAV_ITEMS.map((item, index) => (
                        <li key={item.href} className='hairline border-b'>
                            <SectionLink
                                hash={item.href}
                                onClick={onNavigate}
                                className='flex items-baseline justify-between py-4'
                            >
                                <span className='line-mask'>
                                    <span
                                        className={cn(
                                            'display block text-[clamp(2.75rem,13vw,5.5rem)] wdth-75 transition-transform duration-700 ease-out-expo',
                                            open ? 'translate-y-0' : 'translate-y-full'
                                        )}
                                        style={{ transitionDelay: open ? `${0.18 + index * 0.06}s` : '0s' }}
                                    >
                                        {item.label}
                                    </span>
                                </span>

                                <span className='label text-haze'>{item.index}</span>
                            </SectionLink>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='flex flex-col gap-8'>
                <SectionLink
                    hash={QUOTE_HASH}
                    onClick={onNavigate}
                    className='inline-flex h-14 items-center justify-between bg-signal px-6 text-base font-semibold text-ink'
                >
                    Get a free quote
                    <ArrowRight aria-hidden='true' className='size-5' />
                </SectionLink>

                <div className='label flex items-center justify-between text-haze'>
                    <span>{siteConfig.location}</span>
                    <LocalTime />
                </div>
            </div>
        </div>
    )
}
